import { Plus, MessageCircle, BookOpen, Heart, HelpCircle } from "lucide-react";
import type { Topic } from "@/types";

interface TopicSelectorProps {
  topics: Topic[];
  currentTopic: Topic | null;
  onSelect: (topic: Topic) => void;
  onNewTopic: () => void;
}

/**
 * Sidebar component for selecting chat topics
 *
 * @param topics - List of available topics
 * @param currentTopic - Currently selected topic
 * @param onSelect - Callback when a topic is selected
 * @param onNewTopic - Callback for creating a new topic
 * @returns Topic selection sidebar
 */
export function TopicSelector({
  topics,
  currentTopic,
  onSelect,
  onNewTopic,
}: TopicSelectorProps) {
  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "study":
        return <BookOpen size={14} className="text-study" />;
      case "life":
        return <Heart size={14} className="text-life" />;
      default:
        return <HelpCircle size={14} className="text-other" />;
    }
  };

  return (
    <div className="w-64 shrink-0 hidden md:flex flex-col bg-white rounded-2xl border shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b">
        <button
          onClick={onNewTopic}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors"
        >
          <Plus size={18} />
          New Chat
        </button>
      </div>

      {/* Topics list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin">
        {topics.length === 0 ? (
          <div className="p-4 text-center text-slate-400 text-sm">
            No topics yet
          </div>
        ) : (
          topics.map((topic) => (
            <button
              key={topic.id}
              onClick={() => onSelect(topic)}
              className={`
                w-full flex items-start gap-3 p-3 rounded-xl text-left
                transition-colors duration-150
                ${
                  currentTopic?.id === topic.id
                    ? "bg-primary-100 text-primary-900"
                    : "hover:bg-slate-100 text-slate-700"
                }
              `}
            >
              <MessageCircle size={18} className="mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate text-sm">{topic.name}</p>
                <div className="flex items-center gap-1 mt-1">
                  {getCategoryIcon(topic.category)}
                  <span className="text-xs text-slate-500 capitalize">
                    {topic.category}
                  </span>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

